import type { QueryParams } from "./QueryParams";
import { RequestContext } from "./RequestContext";
import { parseURL } from "@/utils/url";
import Settings from "@/Settings";

/**
 * Creates a new request, and returns the created RequestContext
 */
export abstract class ApiRequest<T> {
  private readonly headers: Headers = new Headers();
  private readonly requestSettings: RequestInit = { credentials: "omit" };
  private readonly queryParams: QueryParams = {};
  private readonly api: "admin" | "health" | "portal";
  private readonly path: Array<string | number>;
  static onAuthError: ((error: Response) => void) | null = null;

  protected constructor(api: "admin" | "health" | "portal", path: Array<string | number>) {
    this.api = api;
    this.path = path;
  }

  public withAuth(): ApiRequest<T> {
    const token = localStorage.getItem("kit.access_token");
    const id_token = localStorage.getItem("kit.id_token");

    if (token) {
      this.headers.set("Authorization", `Bearer ${token}`);
      this.requestSettings.credentials = "include";
    }

    if (id_token) {
      this.headers.set("Id-Token", id_token);
    }

    return this;
  }

  public withApiKey(): ApiRequest<T> {
    const apiKey = Settings.NEWSFEED_API_KEY;
    if (apiKey) {
      this.headers.set("X-Api-Key", apiKey);
    }
    return this;
  }

  public withParams(params?: QueryParams): ApiRequest<T> {
    if (params) {
      Object.keys(params).forEach((param) => {
        if (params[param] !== null && params[param] !== undefined) {
          this.queryParams[param] = Array.isArray(params[param])
            ? (params[param] as []).filter((_elem: string | number) => _elem !== undefined && _elem !== null)
            : params[param];
        }
      });
    }
    return this;
  }

  public disableCache(): ApiRequest<T> {
    this.headers.set("cache-control", "no-store");
    return this;
  }

  public withFormData(formData: FormData): ApiRequest<T> {
    if (formData) {
      this.requestSettings.body = formData;
    }
    return this;
  }

  public withBody(body: Partial<T> | string | any[]): ApiRequest<T> {
    if (body) {
      if (typeof body === "string") {
        this.headers.set("Content-Type", "text/plain");
        this.requestSettings.body = body;
      } else {
        this.headers.set("Content-Type", "application/json");
        this.requestSettings.body = JSON.stringify(body);
      }
    }
    return this;
  }

  private fetch(method: "get" | "put" | "post" | "PATCH" | "delete" = "get"): RequestContext<T> {
    const controller = new AbortController();
    const url = parseURL([this.api, "api", ...this.path], this.queryParams);
    const request = fetch(url, {
      ...this.requestSettings,
      headers: this.headers,
      method,
      signal: controller.signal,
    }).then(async (response) => {
      if (response.status === 401 && ApiRequest.onAuthError) {
        ApiRequest.onAuthError(response);
      }
      return response;
    });
    return new RequestContext<T>(request, controller);
  }

  public get(): RequestContext<T> {
    return this.fetch("get");
  }

  public patch(): RequestContext<T> {
    return this.fetch("PATCH");
  }

  public put(): RequestContext<T> {
    return this.fetch("put");
  }

  public post(): RequestContext<T> {
    return this.fetch("post");
  }

  public delete(): RequestContext<T> {
    return this.fetch("delete");
  }
}

/**
 * A Wrapper for all requests against the HealthApi (/health/api)
 */
export class HealthApiRequest<T> extends ApiRequest<T> {
  public constructor(...path: Array<string | number>) {
    super("health", path);
  }
}

/**
 * A Wrapper for all requests against the AdminApi (/admin/api)
 */
export class AdminApiRequest<T> extends ApiRequest<T> {
  constructor(...path: Array<string | number>) {
    super("admin", path);
  }
}

/**
 * A Wrapper for all requests against the PortalApi (/portal/api)
 */
export class PortalApiRequest<T> extends ApiRequest<T> {
  constructor(...path: Array<string | number>) {
    super("portal", path);
  }
}
